import {
	computeContextMenuTitle,
	ContextMenu,
	DateString,
	TableView,
	VirtualTableViewColumnDefinition,
	useContextMenu,
	TableViewNoResultConfig
} from "client/Components";
import {
	MultiSelectableOptions,
	SelectableItemOptions,
	useIsTouchDevice,
	useRowHover,
	useUserTimezone
} from "client/Hooks";
import {
	DocumentItemViewModel,
	SharePointColumnId
} from "client/Api/Documents/SharePoint/common";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentName, DocumentNameKind } from "../DocumentName";
import {
	ColumnVisibilityState
} from "./SharePointColumnsVisibility/SharePointColumnsVisibility";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { SortingFor } from "client/Data/sorting";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	SharePointDocumentAction,
	SharePointDocumentActionContext,
} from "./actions";
import { columnIdToNameMapping, GridItem } from "./SharePointOverView";
import { DocumentNameWithQuickActions } from "../DocumentNameWithQuickActions";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNotNil } from "client/Misc/utils";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type SharePointTableViewHandle = {
	resetScrollPosition: () => void;
};

export type SharePointTableViewProps = {
	isBusy: boolean;
	hasMoreItems: boolean;
	onLoadMoreItems: () => void;
	sortingFor: SortingFor<GridItem>;
	multiSelection: MultiSelectableOptions;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	columnVisibilities: readonly ColumnVisibilityState[];
	items: readonly GridItem[];
	onOpenItem: (item: DocumentItemViewModel) => void;
	selectionActions: readonly SharePointDocumentAction[];
	actionContext: SharePointDocumentActionContext;
	hasSearch: boolean;
	loadMoreIndicatorVersion: number;
};

export const SharePointTableView = forwardRef(
	(
		props: SharePointTableViewProps,
		ref: Ref<SharePointTableViewHandle>
	): ReactElement => {

		const {
			isBusy,
			hasMoreItems,
			onLoadMoreItems,
			sortingFor,
			multiSelection,
			selectionFor,
			columnVisibilities,
			items,
			onOpenItem,
			selectionActions,
			actionContext,
			hasSearch,
			loadMoreIndicatorVersion
		} = props;

		const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);

		const shouldShowLocationColumn: boolean = actionContext.isInSearchResults;
		const isTouch: boolean = useIsTouchDevice();
		const userTimezone: TimeZone = useUserTimezone();

		const columnVisibilityMap = useMemo(
			(): Map<SharePointColumnId, boolean> => {
				const result: Map<SharePointColumnId, boolean> = fromArray(
					columnVisibilities,
					(x) => x.columnId,
					(x) => x.isVisible
				);

				result.set(SharePointColumnId.path, shouldShowLocationColumn);

				return result;
			},
			[columnVisibilities, shouldShowLocationColumn]
		);

		const columns: readonly VirtualTableViewColumnDefinition[] = useColumnDefinitions(
			columnVisibilityMap,
			sortingFor
		);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					tableRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		const [hoveredRowId, hoverHandler] = useRowHover<string>();

		const noResultConfig = useMemo(
			(): TableViewNoResultConfig | undefined => {
				if (isBusy) {
					return undefined;
				}

				return hasSearch
					? {
						icon: "magnifying-glass",
						title: localize("_NoSearchResults"),
						message: localize("_PleaseTrySearchingWithAnotherTerm")
					}
					: {
						icon: "folder-open",
						title: localize("_NoDocumentsToShow"),
						message: localize("_ThereAreCurrentlyNoDocumentsInThisFolder")
					};
			},
			[hasSearch, isBusy]
		);

		return (
			<>
				<TableView
					isVirtual
					hasNoBottomDelimiter
					columns={columns}
					items={items}
					height="fill"
					itemKey="uniqueKey"
					trackingId="sharepoint-grid"
					multiSelectable={multiSelection}
					ref={tableRef}
					renderAfterItems={
						<>
							<TableView.LoadMoreIndicator
								key={loadMoreIndicatorVersion}
								isLoadingData={isBusy}
								hasMoreItems={hasMoreItems}
								onRequestMoreItems={() => onLoadMoreItems()}
							/>
							{items.length > 0 && (
								<AiAssistantFabSpacing />
							)}
						</>
					}
					noResultConfig={noResultConfig}
				>
					{(x): ReactElement => {
						const selectable: SelectableItemOptions = selectionFor(x);
						const isSelected: boolean = selectable && selectable.isSelected;
						return (
							<TableView.Row
								selectable={selectable}
								onContextMenu={contextMenuHandler}
								trackingId={x.uniqueKey}
								onIsHoverChange={(isHover: boolean) => hoverHandler(x.uniqueKey, isHover)}
							>
								{columnVisibilityMap.get(SharePointColumnId.name) && (
									<TableView.Cell>
										<DocumentNameWithQuickActions
											selectionActions={selectionActions}
											actionContext={{...actionContext, selection: [x.model]}}
											isTouch={isTouch}
											isSelected={isSelected}
											isRowHovered={hoveredRowId === x.uniqueKey}
											name={x.name}
										>
											<DocumentName
												name={x.name}
												documentKind={x.model.itemType}
												mode={{
													kind: DocumentNameKind.Default,
													hasBookmark: isNotNil(x.model.bookmarkId),
													isInfected: x.isInfected
												}}
												onClick={() => onOpenItem(x.model)}
											/>
										</DocumentNameWithQuickActions>
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.dateCreated) && (
									<TableView.Cell>
										<DateString
											value={x.createdOn}
											format="RelativeTimestamp"
											timezone={userTimezone}
										/>
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.dateModified) && (
									<TableView.Cell>
										<DateString
											value={x.modifiedOn}
											format="RelativeTimestamp"
											timezone={userTimezone}
										/>
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.createdBy) && (
									<TableView.Cell>
										{x.createdBy}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.modifiedBy) && (
									<TableView.Cell>
										{x.modifiedBy}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.extension) && (
									<TableView.Cell>
										{x.extension}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.fileSize) && (
									<TableView.Cell>
										{getDisplayFriendlySize(x.size)}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.id) && (
									<TableView.Cell>
										{x.id}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.checkInComment) && (
									<TableView.Cell>
										{x.checkInComment}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.labels) && (
									<TableView.Cell>
										{x.labels}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.labelSetting) && (
									<TableView.Cell>
										{x.labelSetting}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.labelApplied) && (
									<TableView.Cell>
										{x.labelApplied && (
											<DateString
												value={x.labelApplied}
												format="RelativeTimestamp"
												timezone={userTimezone}
											/>
										)}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.labelAppliedBy) && (
									<TableView.Cell>
										{x.labelAppliedBy}
									</TableView.Cell>
								)}

								{columnVisibilityMap.get(SharePointColumnId.path) && (
									<TableView.Cell>
										{x.location}
									</TableView.Cell>
								)}
							</TableView.Row>
						);
					}}
				</TableView>
				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={selectionActions}
						context={actionContext}
						trackingId="sharepoint-context-menu"
						title={computeContextMenuTitle(
							actionContext.selection,
							(x) => x.name
						)}
					/>
				)}
			</>
		);
	}
);
SharePointTableView.displayName = "SharePointTableView";

function useColumnDefinitions(
	columnVisibilityMap: Map<SharePointColumnId, boolean>,
	sortingFor: SortingFor<GridItem>
): readonly VirtualTableViewColumnDefinition[] {

	return useMemo(
		(): readonly VirtualTableViewColumnDefinition[] => {
			const result: VirtualTableViewColumnDefinition[] = [];

			if (columnVisibilityMap.get(SharePointColumnId.name)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.name]),
					sortable: sortingFor("name"),
					trackingId: "name",
					width: 400,
					minWidth: 300
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.dateCreated)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.dateCreated]),
					sortable: sortingFor("createdOn"),
					trackingId: "created-on",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.dateModified)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.dateModified]),
					sortable: sortingFor("modifiedOn"),
					trackingId: "modified-on",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.createdBy)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.createdBy]),
					trackingId: "created-by",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.modifiedBy)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.modifiedBy]),
					trackingId: "modified-by",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.extension)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.extension]),
					sortable: sortingFor("extension"),
					trackingId: "extension",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.fileSize)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.fileSize]),
					sortable: sortingFor("size"),
					trackingId: "size",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.id)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.id]),
					trackingId: "id",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.checkInComment)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.checkInComment]),
					trackingId: "check-in-comment",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.labels)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.labels]),
					trackingId: "labels",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.labelSetting)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.labelSetting]),
					trackingId: "label-setting",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.labelApplied)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.labelApplied]),
					trackingId: "label-applied",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.labelAppliedBy)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.labelAppliedBy]),
					trackingId: "label-applied-by",
					width: 150
				});
			}

			if (columnVisibilityMap.get(SharePointColumnId.path)) {
				result.push({
					title: localize(columnIdToNameMapping[SharePointColumnId.path]),
					maxWidth: 700,
					trackingId: "path",
					width: 150
				});
			}

			return result;
		},
		[columnVisibilityMap, sortingFor]
	);
}
